import { Id } from 'NSwag/ServerClient/Client.nswag';

/**
 * The class that is used to represent helper utility functions.
 */
class Util {
    /**
     * Converts a Id object into a string representation.
     * @param id - The Identifier object.
     */
    public static IdToString = (id: Id): string => {
        if (id) {
            return `${id.name}:${id.providerName}:${id.resourceType}:${id.centerId}`;
        } else {
            return '';
        }
    }

    /**
     * A function to take a known type at runtime and convert it to that instance.
     * @param constr - The constructor for the known type.
     * @param data - The JSON generic object to convert to the known type.
     * @param strictMode - Typescript compiler mode.
     * @param args - The arguments of the known type constructor.
     */
    public static hydrate<T>(constr: { new(...args: any[]): T }, data: Record<string, any>, strictMode = true, ...args: any[]): T {
        const instance: any = new constr(...args);
        const dataInstance: any = new constr(...args);
        dataInstance.init(data);
        instance.init({});

        for (const key in data) {
            if (!strictMode || Object.prototype.hasOwnProperty.call(instance, key)) {
                instance[key] = dataInstance[key];
            }
        }

        return instance;
    }

    /**
     * Creates a Deep Copy of an already instantiated Index Signature, Array, or Date object.
     * @param target - The object to be copied.
     */
    public static DeepCopy = <T>(target: T): T => {
        if (target === null) {
            return target;
        }

        if (target instanceof Date) {
            return new Date(target.getTime()) as any;
        }

        if (target instanceof Array) {
            const cp = [] as any[];
            (target as any[]).forEach((v) => { cp.push(v); });
            return cp.map((n: any) => Util.DeepCopy<any>(n)) as any;
        }

        if (typeof target === 'object' && target !== {}) {
            const cp = { ...(target as { [key: string]: any }) } as { [key: string]: any };
            Object.keys(cp).forEach(k => {
                cp[k] = Util.DeepCopy<any>(cp[k]);
            });
            return cp as T;
        }

        return target;
    }

    /**
     * Retrieves the value or returns the default value if the original value was undefined or null.
     * @param value The value to retrieve if available.
     * @param defaultValue The value to return if the original value is undefined or null.
     */
    public static getValueOrDefault = <TValue>(value: TValue | null | undefined, defaultValue: TValue): TValue => {
        return value === undefined || value === null ? defaultValue : value;
    }
}

export default Util;
